import React, { useState, useEffect, useRef } from 'react';
import * as faceapi from 'face-api.js';
import './FaceSwapUpload.css';
import { useTooltipEvents } from '../hooks/useTooltipEvents';
import { useTooltip } from './TooltipProvider';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

// Demo images from CDN
import demo1 from '../assets/images/demo-1.png';
import demo2 from '../assets/images/demo-2.png';
import demo3 from '../assets/images/demo-3.png';
// const demo1 = '../assets/images/demo-1.png';
// const demo2 = '../assets/images/demo-2.png';
// const demo3 = '../assets/images/demo-3.png';

// 定义接口
interface DetectedFace {
  detection: faceapi.FaceDetection;
  landmarks: faceapi.FaceLandmarks68;
  descriptor: Float32Array;
  box: { x: number; y: number; width: number; height: number };
  croppedImageUrl?: string; // 裁剪后的圆形人脸图片URL
}

interface UploadedFile {
  file: File;
  url: string;
  type: 'image' | 'video';
}

interface SubmitFaceSwapRequest {
  source_url: string;
  detected_faces: string[]
  replacement_faces: string[]
  type: string;
}

interface Props {
  onTaskSubmitted?: () => void;
}

// 应用状态类型
type AppState = 'upload' | 'processing' | 'editing';

const FaceSwapUpload: React.FC<Props> = ({ onTaskSubmitted }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [appState, setAppState] = useState<AppState>('upload');
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [detectedFaces, setDetectedFaces] = useState<DetectedFace[]>([]);
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [progressPercent, setProgressPercent] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [targetFaces, setTargetFaces] = useState<{ [key: number]: File | null }>({});
  const [targetFaceImages, setTargetFaceImages] = useState<{ [key: number]: string | null }>({});
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const { user, isLoggedIn } = useAuth();
  const navigate = useNavigate();

  // Tooltip context
  const { hideTooltip } = useTooltip();

  // Tooltip事件hooks
  const closeButtonTooltip = useTooltipEvents("Back to Upload");
  const uploadFaceTooltip = useTooltipEvents("Upload Face");
  const removeTargetTooltip = useTooltipEvents("Remove Target Face");

  // 在组件内部定义缓存变量（或可提升为 context/ref）
  const uploadedFileCache = useRef<{ file: File; url: string } | null>(null);
  const detectedFacesCache = useRef<Map<number, {
    imageUrlHint: string | undefined; face_url: string; descriptor: number[]; box: any 
  }>>(
    new Map()
  );
  const replacementFacesCache = useRef<Map<number, string>>(new Map());

  // 清除缓存的方法（可在 resetToUpload 中调用）
  const clearUploadCache = () => {
    // uploadedFileCache.current = null;
    // detectedFacesCache.current.clear();
    replacementFacesCache.current.clear();
  };

  // 加载face-api.js模型
  useEffect(() => {
    const loadModels = async () => {
      try {
        setProcessingMessage('Loading AI models...');
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri('/models'),
          faceapi.nets.ssdMobilenetv1.loadFromUri('/models'),
          faceapi.nets.faceLandmark68Net.loadFromUri('/models'),
          faceapi.nets.faceRecognitionNet.loadFromUri('/models'),
        ]);
        setIsModelLoaded(true);
        setProcessingMessage('');
      } catch (error) {
        console.error('模型加载失败:', error);
        setProcessingMessage('Failed to load AI models');
      }
    };

    loadModels();
  }, []);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  // 处理demo图片点击
  const handleDemoClick = (demoPath: string) => {
    if (!isLoggedIn) {
      // 未登录，跳转到登录页并保存当前路径
      navigate('/login', { state: { from: { pathname: '/pricing' } } });
      return;
    }
    
    fetch(demoPath, {
      mode: "cors",
      credentials: "omit"
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.blob();
      })
      .then(blob => {
        const file = new File([blob], 'demo.png', { type: 'image/png' });
        processFile(file);
      })
      .catch(error => {
        console.error('Failed to load demo image:', error);
        alert('Failed to load demo image. Please try uploading your own image.');
      });
  };

  // 处理文件上传和人脸检测
  const processFile = async (file: File) => {
    if (!isModelLoaded) {
      alert('AI models are still loading, please try again later');
      return;
    }

    const fileType = file.type.startsWith('image/') ? 'image' : 'video';
    const fileUrl = URL.createObjectURL(file);
    
    setUploadedFile({ file, url: fileUrl, type: fileType });
    setAppState('processing');
    setProcessingMessage('Detecting faces...');
    setProgressPercent(0);
    setShowProgress(false);

    try {
      if (fileType === 'image') {
        await detectFacesInImage(fileUrl);
      } else {
        // 对于视频，我们可以提取第一帧进行人脸检测
        await detectFacesInVideo(fileUrl);
      }
    } catch (error) {
      console.error('Face detection failed:', error);
      setProcessingMessage('Face detection failed, please try again');
      setTimeout(() => {
        setAppState('upload');
        setProcessingMessage('');
        setShowProgress(false);
        setProgressPercent(0);
      }, 2000);
    }
  };

  // 优化的人脸检测函数 - 使用SSD MobileNet作为主检测器
  const detectAllFacesWithMultipleDetectors = async (input: HTMLImageElement | HTMLCanvasElement) => {
    try {
      // 直接使用SSD MobileNet，它对小人脸检测更准确
      const detections = await faceapi
        .detectAllFaces(input, new faceapi.SsdMobilenetv1Options({
          minConfidence: 0.2,  // 降低置信度阈值以检测更多人脸
          maxResults: 20       // 增加最大检测数量
        }))
        .withFaceLandmarks()
        .withFaceDescriptors();

      return detections;
    } catch (error) {
      console.error('SSD detection failed, falling back to TinyFaceDetector:', error);
      
      // If SSD fails, fall back to TinyFaceDetector
      try {
        const detections = await faceapi
          .detectAllFaces(input, new faceapi.TinyFaceDetectorOptions({
            inputSize: 608,      // 进一步提高输入尺寸
            scoreThreshold: 0.2  // 降低阈值
          }))
          .withFaceLandmarks()
          .withFaceDescriptors();
          
        return detections;
      } catch (fallbackError) {
        console.error('All detectors failed:', fallbackError);
        return [];
      }
    }
  };

  // 图片人脸检测
  const detectFacesInImage = async (imageUrl: string) => {
    return new Promise<void>((resolve, reject) => {
      const img = new Image();
      img.onload = async () => {
        try {
          const detections = await detectAllFacesWithMultipleDetectors(img);

          if (detections.length === 0) {
            setProcessingMessage('No faces detected. Please upload an image with clear faces.');
            setTimeout(() => {
              setAppState('upload');
              setProcessingMessage('');
              setShowProgress(false);
              setProgressPercent(0);
            }, 3000);
            resolve();
            return;
          }

          const faces: DetectedFace[] = detections.map(detection => {
            const box = {
              x: detection.detection.box.x,
              y: detection.detection.box.y,
              width: detection.detection.box.width,
              height: detection.detection.box.height
            };
            
            // 裁剪圆形人脸
            const croppedImageUrl = cropCircularFace(img, box);
            
            return {
              detection: detection.detection,
              landmarks: detection.landmarks,
              descriptor: detection.descriptor,
              box,
              croppedImageUrl
            };
          });

          setDetectedFaces(faces);
          setProcessingMessage(`Detected ${faces.length} face${faces.length > 1 ? 's' : ''}`);
          
          setTimeout(() => {
            setAppState('editing');
            setProcessingMessage('');
            setShowProgress(false);
            setProgressPercent(0);
          }, 1500);
          
          resolve();
        } catch (error) {
          reject(error);
        }
      };
      img.onerror = reject;
      img.src = imageUrl;
    });
  };

  // 计算两个人脸描述符的欧氏距离（相似度）
  const calculateFaceSimilarity = (descriptor1: Float32Array, descriptor2: Float32Array): number => {
    // 使用face-api.js内置的euclideanDistance函数更准确
    const distance = faceapi.euclideanDistance(descriptor1, descriptor2);
    // face-api.js的距离通常在0-1.2之间，距离越小越相似
    // 转换为相似度：距离0.6以下认为是同一人
    return distance;
  };

  // 视频人脸检测（多帧采样）
  const detectFacesInVideo = async (videoUrl: string) => {
    return new Promise<void>((resolve, reject) => {
      const video = document.createElement('video');
      video.src = videoUrl;
      video.onloadeddata = async () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Unable to create canvas context'));
            return;
          }

          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;

          // Get total frames and duration
          const duration = video.duration;
          const frameRate = 30; // Assuming 30fps
          const totalFrames = Math.floor(duration * frameRate);
          
          // Calculate sample frames: first frame, last frame, interval of 30 frames
          const sampleFrames: number[] = [0]; // First frame
          
        // Sample at 30 frame intervals
        for (let frame = 30; frame < totalFrames - 30; frame += 30) {
          sampleFrames.push(frame);
        }
          
          // Last frame
          if (totalFrames > 30) {
            sampleFrames.push(totalFrames - 1);
          }

          setProcessingMessage(`Analyzing faces in video... (${sampleFrames.length} key frames)`);
          setShowProgress(true);
          setProgressPercent(0);

          const allDetectedFaces: DetectedFace[] = [];

          // Detect faces frame by frame
          for (let i = 0; i < sampleFrames.length; i++) {
            const frameNumber = sampleFrames[i];
            const timePosition = frameNumber / frameRate;
            
            // Set video time position
            video.currentTime = timePosition;
            
            // Wait for video to seek to specified time
            await new Promise<void>((frameResolve) => {
              const onSeeked = () => {
                video.removeEventListener('seeked', onSeeked);
                frameResolve();
              };
              video.addEventListener('seeked', onSeeked);
              
              // Add timeout protection
              setTimeout(() => {
                video.removeEventListener('seeked', onSeeked);
                frameResolve();
              }, 1000);
            });

            // Ensure canvas size is correct
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            
            // Draw current frame to canvas
            ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

            // Detect faces in current frame
            const detections = await detectAllFacesWithMultipleDetectors(canvas);
            
            console.log(`Frame ${i + 1} (time: ${timePosition.toFixed(2)}s): Detected ${detections.length} face(s)`);

            // Process detected faces
            for (const detection of detections) {
              const box = {
                x: detection.detection.box.x,
                y: detection.detection.box.y,
                width: detection.detection.box.width,
                height: detection.detection.box.height
              };
              
              const croppedImageUrl = cropCircularFace(canvas as any, box);
              
              const newFace: DetectedFace = {
                detection: detection.detection,
                landmarks: detection.landmarks,
                descriptor: detection.descriptor,
                box,
                croppedImageUrl
              };

              // Check if similar to existing faces
              let isSimilar = false;
              let minDistance = Infinity;
              for (const existingFace of allDetectedFaces) {
                const distance = calculateFaceSimilarity(newFace.descriptor, existingFace.descriptor);
                minDistance = Math.min(minDistance, distance);
                if (distance < 0.6) { // Distance < 0.6 means same person
                  isSimilar = true;
                  break;
                }
              }

              // Only add non-similar faces (deduplication)
              if (!isSimilar) {
                console.log(`Adding new face (total: ${allDetectedFaces.length + 1}), min distance: ${minDistance.toFixed(3)}`);
                allDetectedFaces.push(newFace);
              } else {
                console.log(`Skipping similar face, distance: ${minDistance.toFixed(3)} < 0.6`);
              }
            }

            // Update progress
            const progressPercent = Math.round(((i + 1) / sampleFrames.length) * 100);
            setProgressPercent(progressPercent);
            setProcessingMessage(`Analyzing faces in video...`);
          }

          if (allDetectedFaces.length === 0) {
            setProcessingMessage('No faces detected in video');
            setTimeout(() => {
              setAppState('upload');
              setProcessingMessage('');
              setShowProgress(false);
              setProgressPercent(0);
            }, 3000);
            resolve();
            return;
          }

          setDetectedFaces(allDetectedFaces);
          setProcessingMessage(`Detected ${allDetectedFaces.length} unique face${allDetectedFaces.length > 1 ? 's' : ''}`);
          
          setTimeout(() => {
            setAppState('editing');
            setProcessingMessage('');
            setShowProgress(false);
            setProgressPercent(0);
          }, 1500);
          
          resolve();
        } catch (error) {
          reject(error);
        }
      };
      video.onerror = reject;
    });
  };

  // 重置到上传状态
  const resetToUpload = () => {
    // 隐藏tooltip
    hideTooltip();
    
    setAppState('editing');
    // setUploadedFile(null);
    // setDetectedFaces([]);
    // setProcessingMessage('');
    setTargetFaces({});
    setTargetFaceImages({});
    clearUploadCache();
    setProgressPercent(0);
    setShowProgress(false);
  };

  // 删除上传
  const clearToUpload = () => {
    // 隐藏tooltip
    hideTooltip();
    
    setAppState('upload');
    setUploadedFile(null);
    setDetectedFaces([]);
    setProcessingMessage('');
    setTargetFaces({});
    setTargetFaceImages({});
  };

  // 裁剪圆形人脸
  const cropCircularFace = (
    imageElement: HTMLImageElement | HTMLCanvasElement,
    faceBox: { x: number; y: number; width: number; height: number }
  ): string => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    // ✅ 获取原始分辨率（关键：避免模糊）
    const naturalWidth =
      imageElement instanceof HTMLImageElement
        ? imageElement.naturalWidth
        : imageElement.width;

    const displayWidth =
      imageElement instanceof HTMLImageElement
        ? imageElement.width
        : imageElement.width;

    const scale = naturalWidth / displayWidth;

    // ✅ 放大裁剪区域（提升清晰度）
    const paddingRatio = 0.4; // 比你原来更大，避免脸太紧
    const size = Math.max(faceBox.width, faceBox.height);
    const paddedSize = size * (1 + paddingRatio);

    const radius = paddedSize / 2;

    // ✅ 输出更高清（关键）
    const outputSize = 512; // 128 → 256 / 512 更清晰
    canvas.width = outputSize;
    canvas.height = outputSize;

    // 中心点（按原图比例）
    const centerX = (faceBox.x + faceBox.width / 2) * scale;
    const centerY = (faceBox.y + faceBox.height / 2) * scale;

    const cropX = centerX - radius * scale;
    const cropY = centerY - radius * scale;

    // ✅ 高质量渲染
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // 圆形裁剪
    ctx.beginPath();
    ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();

    ctx.drawImage(
      imageElement,
      cropX,
      cropY,
      paddedSize * scale,
      paddedSize * scale,
      0,
      0,
      outputSize,
      outputSize
    );

    // ✅ 更高质量导出
    return canvas.toDataURL('image/jpeg', 0.95);
  };

  // 检测目标人脸图片中的第一个人脸
  const detectTargetFace = async (file: File, faceIndex: number): Promise<void> => {
    if (!isModelLoaded) return;

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = async () => {
        try {
          const detections = await detectAllFacesWithMultipleDetectors(img);
          
          if (detections.length > 0) {
            // 使用第一个检测到的人脸
            const firstFace = detections[0];
            const box = {
              x: firstFace.detection.box.x,
              y: firstFace.detection.box.y,
              width: firstFace.detection.box.width,
              height: firstFace.detection.box.height
            };
            
            // 裁剪圆形人脸
            const croppedImageUrl = cropCircularFace(img, box);
            
            // 保存裁剪后的人脸图片
            setTargetFaceImages(prev => ({
              ...prev,
              [faceIndex]: croppedImageUrl
            }));
          } else {
            // 如果没有检测到人脸，使用原图的中心区域
            const centerBox = {
              x: img.width * 0.25,
              y: img.height * 0.25,
              width: img.width * 0.5,
              height: img.height * 0.5
            };
            const croppedImageUrl = cropCircularFace(img, centerBox);
            
            setTargetFaceImages(prev => ({
              ...prev,
              [faceIndex]: croppedImageUrl
            }));
          }
          
          resolve();
        } catch (error) {
          console.error('Target face detection failed:', error);
          reject(error);
        }
      };
      
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  // 处理目标人脸上传
  const handleTargetFaceUpload = async (e: React.ChangeEvent<HTMLInputElement>, faceIndex: number) => {
    const file = e.target.files?.[0];
    if (file) {
      setTargetFaces(prev => ({
        ...prev,
        [faceIndex]: file
      }));
      
      // Detect and crop target face
      try {
        await detectTargetFace(file, faceIndex);
        console.log(`Uploaded and processed target face ${faceIndex + 1}:`, file);
      } catch (error) {
        console.error(`Failed to process target face ${faceIndex + 1}:`, error);
      }
    }
  };

  // Remove target face
  const removeTargetFace = (faceIndex: number) => {
    setTargetFaces(prev => ({
      ...prev,
      [faceIndex]: null
    }));
    setTargetFaceImages(prev => ({
      ...prev,
      [faceIndex]: null
    }));
    console.log(`Removed target face ${faceIndex + 1}`);
  };

  // 检查是否有任何目标脸被上传
  const hasAnyTargetFace = () => {
    return Object.values(targetFaces).some(file => file !== null);
  };

  //r2Upload
  const uploadToR2 = async (file: File): Promise<string> => {
    try {
      const token = localStorage.getItem('glistar_token');
      // Simulate calling backend upload API, return R2 URL
      const response = await fetch(`${process.env.REACT_APP_API_URL}/site/config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({type:file.type}),
      });

      const data = await response.json();

      if (!response.ok) throw new Error('Upload failed');

      // 2. Upload directly to R2
      const uploadResponse = await fetch(data.data.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          // 'Authorization': `Bearer ${token}`,
          'Content-Type': file.type,
        },
      });

      console.log(response,data,'uplodfile')

      if (uploadResponse.ok) {
        return `https://cdn.glistar.ai/${data.data.fileKey}`; 
      }  else {
        throw new Error('R2 upload failed');
      }

    } catch (error) {
      console.error('R2 upload error:', error);
      throw error;
    }
  };

  // Define inside component
  const handleStartFaceSwap = async () => {
    if (!isLoggedIn) {
      // 未登录，跳转到登录页并保存当前路径
      navigate('/login', { state: { from: { pathname: '/' } } });
      return;
    }

    if (!uploadedFile || detectedFaces.length === 0) return;

    setAppState('processing');
    setProcessingMessage('Uploading files to R2...');
    setShowProgress(true);
    setProgressPercent(0);

    try {
      let sourceMediaUrl: string;

      // ✅ Step 1: Only upload source file if not uploaded or file is different
      if (
        !uploadedFileCache.current ||
        uploadedFileCache.current.file !== uploadedFile.file
      ) {
        setProcessingMessage('Uploading source media...');
        sourceMediaUrl = await uploadToR2(uploadedFile.file);
        uploadedFileCache.current = { file: uploadedFile.file, url: sourceMediaUrl };
      } else {
        sourceMediaUrl = uploadedFileCache.current.url;
      }
      // Step 1: Upload source media file
      // setProcessingMessage('Uploading source media...');
      // const sourceMediaUrl = await uploadToR2(uploadedFile.file);
      setProgressPercent(20);

      // Step 2: Upload detected_faces (cropped face images + descriptor)
      setProcessingMessage('Uploading detected faces...');
      const detectedFacePromises = detectedFaces.map(async (face, idx) => {
        const cached = detectedFacesCache.current.get(idx);
        if (cached && face.croppedImageUrl === cached.imageUrlHint) {
          // Assume you can use croppedImageUrl for comparison (note blob URL may differ each time)
          return cached;
        }
        // Convert cropped image to Blob and upload
        const response = await fetch(face.croppedImageUrl!);
        const blob = await response.blob();
        const faceFile = new File([blob], `detected-face-${idx}.png`, { type: 'image/png' });
        const faceUrl = await uploadToR2(faceFile);

        const result = {
          face_url: faceUrl,
          descriptor: Array.from(face.descriptor), // Float32Array → number[]
          box: face.box,
          imageUrlHint: face.croppedImageUrl,
        };

        detectedFacesCache.current.set(idx, result);
        return result;
      });

      const detectedFaceData = await Promise.all(detectedFacePromises);

      const sourceimgurls = detectedFaceData.map(item => item.face_url);
      setProgressPercent(60);

      // Step 3: Upload replacement_faces
      setProcessingMessage('Uploading replacement faces...');
      // const replacementFacePromises = Object.keys(targetFaces)
      //   .map(key => Number(key))
      //   .filter(idx => targetFaces[idx])
      //   .map(async (idx) => {
      //     const file = targetFaces[idx]!;
      //     const faceUrl = await uploadToR2(file);
      //     return {
      //       index: idx,
      //       face_url: faceUrl,
      //     };
      //   });

      const targetIndices = Object.keys(targetFaces)
      .map(key => Number(key))
      .filter(idx => targetFaces[idx]);

      const replacementFacePromises = targetIndices.map(async (idx) => {
        const file = targetFaces[idx]!;
        const cachedUrl = replacementFacesCache.current.get(idx);

        let faceUrl: string;
        if (cachedUrl && /* custom validation logic */ true) {
          faceUrl = cachedUrl;
        } else {
          faceUrl = await uploadToR2(file);
          replacementFacesCache.current.set(idx, faceUrl);
        }

        return { index: idx, face_url: faceUrl };
      });

      const replacementFaceData = await Promise.all(replacementFacePromises);

      const targetimgurls = replacementFaceData.map(item => item.face_url);
      setProgressPercent(90);

      // Step 4: Construct request body
      const payload: SubmitFaceSwapRequest = {
        source_url: sourceMediaUrl,
        detected_faces: sourceimgurls,
        replacement_faces: targetimgurls,
        type: uploadedFile.type
      };

      console.log('Submitting to backend:', payload);

      // Step 5: Submit to main endpoint
      setProcessingMessage('Submitting job...');

      const token = localStorage.getItem('glistar_token');
      const submitResponse = await fetch(`${process.env.REACT_APP_API_URL}/face-swap/task`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify(payload),
      });

      if (!submitResponse.ok) throw new Error('Job submission failed');

      const result = await submitResponse.json();
      setProcessingMessage('Success! Job submitted.');
      setProgressPercent(100);

      // Navigate or show result page
      setTimeout(() => {
        if(result.status === 200) {
          alert(`Face swap job submitted! Job ID: ${result.data.task_id}`);
        } else {
          alert(result.message);
        }

        // ✅ 关键：通知父组件任务已提交
        if (onTaskSubmitted) {
          onTaskSubmitted();
        }
        
        resetToUpload();
      }, 1500);

    } catch (error) {
      console.error('Face swap submission failed:', error);
      setProcessingMessage('Failed to submit job.');
      setTimeout(() => {
        setAppState('editing');
        setShowProgress(false);
      }, 3000);
    }
  };

  // 渲染上传状态UI
  const renderUploadState = () => (
        <div className="upload-wrapper">
          <div 
            className={`upload-area ${isDragOver ? 'drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="upload-content">
              <div className="upload-main">
                <p className="upload-text">Drop image or video here or click to select</p>
                
            <button 
              className="upload-button"
              onClick={() => {
                if (!isLoggedIn) {
                  // 未登录，跳转到登录页并保存当前路径
                  navigate('/login', { state: { from: { pathname: '/pricing' } } });
                  return;
                }
                document.getElementById('file-upload')?.click()
              }}
            >
                  Upload Image/Video
                </button>
                
                <input
                  type="file"
                  id="file-upload"
                  className="file-input"
                  multiple
                  accept=".png,.webp,.jpeg,.jpg,.mp4,.mov,image/png,image/webp,image/jpeg,image/jpg,video/mp4,video/quicktime"
                  onChange={handleFileSelect}
                />
                
                <div className="demo-images">
              <div 
                className="demo-image" 
                style={{ backgroundImage: `url(${demo1})` }}
                onClick={() => handleDemoClick(demo1)}
              >
                    <span className="demo-label">Demo</span>
                  </div>
              <div 
                className="demo-image" 
                style={{ backgroundImage: `url(${demo2})` }}
                onClick={() => handleDemoClick(demo2)}
              >
                    <span className="demo-label">Demo</span>
                  </div>
              <div 
                className="demo-image" 
                style={{ backgroundImage: `url(${demo3})` }}
                onClick={() => handleDemoClick(demo3)}
              >
                    <span className="demo-label">Demo</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
  );

  // 渲染处理状态UI
  const renderProcessingState = () => (
    <div className="upload-wrapper">
      <div className="processing-area">
        <div className="processing-content">
          <div className="processing-spinner"></div>
          <p className="processing-text">{processingMessage}</p>
          {showProgress && (
            <div className="progress-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${progressPercent}%` }}
                ></div>
              </div>
              <span className="progress-text">{progressPercent}%</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // 渲染编辑状态UI
  const renderEditingState = () => (
    <div className="editing-wrapper">
      <div className="editing-header">
        <h3 className="editing-title">Face Swap Editor</h3>
      </div>
      
      <div className="editing-content">
        <div className="source-panel">
          <div className="panel-header">
            <h4 className="panel-title">Original {uploadedFile?.type === 'image' ? 'Image' : 'Video'}</h4>
            <span className="face-count">{detectedFaces.length} face{detectedFaces.length !== 1 ? 's' : ''} detected</span>
          </div>
          
          <div className="source-preview">
            {uploadedFile?.type === 'image' ? (
              <img 
                ref={imageRef}
                src={uploadedFile.url} 
                alt="Source" 
                className="source-image"
              />
            ) : (
              <video 
                src={uploadedFile?.url} 
                className="source-video" 
                controls
              />
            )}
            <canvas 
              ref={canvasRef}
              className="face-overlay"
            />
            <button 
              className="close-button"
              onClick={clearToUpload}
              {...closeButtonTooltip}
            >
              ×
            </button>
          </div>
        </div>

        <div className="target-panel">
          <div className="panel-header">
            <h4 className="panel-title">Target Face</h4>
          </div>
          
          <div className="face-pairs">
            {detectedFaces.map((face, index) => (
              <div key={index} className="face-pair">
                <div className="source-face">
                  <div className="circular-face">
                    {face.croppedImageUrl && (
                      <img 
                        src={face.croppedImageUrl} 
                        alt={`Detected face ${index + 1}`}
                        className="cropped-face-image"
                      />
                    )}
                  </div>
                </div>
                
                <div className="swap-arrow">
                  <span>→</span>
                </div>
                
                <div className="target-face">
                  {targetFaceImages[index] ? (
                    <div className="target-face-container">
                      <div className={`target-face-slot has-image`}>
                        <div className="circular-face">
                          <img 
                            src={targetFaceImages[index]!} 
                            alt={`Target face ${index + 1}`}
                            className="cropped-face-image"
                          />
                        </div>
                        <input
                          type="file"
                          accept=".png,.webp,.jpeg,.jpg,image/png,image/webp,image/jpeg,image/jpg"
                          className="target-file-input"
                          onChange={(e) => handleTargetFaceUpload(e, index)}
                        />
                      </div>
                      <button 
                        className="target-remove-button"
                        onClick={() => removeTargetFace(index)}
                        {...removeTargetTooltip}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className={`target-face-slot`} {...uploadFaceTooltip}>
                      <div className="upload-target">
                        <span className="upload-icon">+</span>
                        <span className="upload-text">Upload Face</span>
                      </div>
                      <input
                        type="file"
                        accept=".png,.webp,.jpeg,.jpg,image/png,image/webp,image/jpeg,image/jpg"
                        className="target-file-input"
                        onChange={(e) => handleTargetFaceUpload(e, index)}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="editing-actions">
        <button 
          className={`action-button ${hasAnyTargetFace() ? 'primary' : 'primary disabled'}`}
          disabled={!hasAnyTargetFace()}
          onClick={handleStartFaceSwap}
        >
          {hasAnyTargetFace() ? 'Start Face Swap' : 'Select Target Face'}
        </button>
      </div>
    </div>
  );

  return (
    <section className="face-swap-upload">
      <div className="upload-container">
        {appState === 'upload' && renderUploadState()}
        {appState === 'processing' && renderProcessingState()}
        {appState === 'editing' && renderEditingState()}
      </div>
    </section>
  );
};

export default FaceSwapUpload;